import {
  getWithdrawals, getKycUsers, approveWithdrawal, rejectWithdrawal,
  checkWithdrawalStatus, resolveWithdrawal, approveKyc, rejectKyc,
} from '../admin-api';
import type { WithdrawalRow, KycUser, WithdrawalSettleOutcome } from '../admin-api';
import {
  esc, adminTable, pagination, badge, formatDate, formatMoney,
  showConfirm, showPrompt, statusBadge,
} from '../admin-ui';
import { ApiError } from '../../../core/network/api';
import { cmsErrorText } from './admin-cms';

type Tab = 'withdrawals' | 'kyc';

const PAGE_SIZE = 25;
const WD_FILTERS = ['', 'PENDING', 'PROCESSING', 'APPROVED', 'REJECTED', 'FAILED'];
/** The queue opens on what needs a decision; '' is every submitted status. */
const KYC_FILTERS = ['PENDING', 'APPROVED', 'REJECTED', ''];

/** What check-status / resolve outcomes mean to an operator. */
const OUTCOME_TEXT: Record<WithdrawalSettleOutcome, string> = {
  approved: 'Paid — marked approved and invoiced.',
  late_approved: 'Paid late — marked approved; check the review queue.',
  failed: 'Declined by the gateway — funds released to the player.',
  pending: 'Still processing at the gateway — nothing changed.',
  review: 'Ambiguous result — left for manual review.',
  duplicate: 'Already settled — nothing changed.',
  noop: 'Nothing to change.',
  not_found: 'The gateway does not know this payout.',
};

/** Player label from core's nested `user` — the row has no top-level username. */
export function withdrawalPlayer(r: WithdrawalRow): string {
  return r.user?.username || r.user?.email || r.email || r.userId;
}

/**
 * The actions core accepts for a row in this status (2026-10-19). Only PENDING
 * rows used to get buttons, so a payout stuck in PROCESSING had no way out of
 * the panel even though core can poll it, record it paid, or resolve it.
 *
 * - approve: PENDING/PROCESSING, except a PENDING row core created itself
 *   (`orderMerchantId` set) — that was never sent, core answers 409 and wants
 *   a reject instead, so the button is not offered.
 * - reject: PENDING only (core's CAS); PROCESSING is in flight.
 * - check / resolve: need a gateway order; resolve is PROCESSING only.
 */
export function withdrawalActions(r: WithdrawalRow): Array<'approve' | 'reject' | 'check' | 'resolve'> {
  const sent = Boolean(r.orderMerchantId);
  if (r.status === 'PENDING') return [...(sent ? [] : ['approve' as const]), 'reject', ...(sent ? ['check' as const] : [])];
  if (r.status === 'PROCESSING') return [...(sent ? ['check' as const] : []), 'approve', ...(sent ? ['resolve' as const] : [])];
  return [];
}

const ACTION_BUTTON: Record<ReturnType<typeof withdrawalActions>[number], (id: string) => string> = {
  approve: id => `<button data-wd-approve="${esc(id)}" class="btn btn-gold text-[11px] px-2 py-1">Mark paid</button>`,
  reject: id => `<button data-wd-reject="${esc(id)}" class="btn btn-stone text-[11px] px-2 py-1">Reject</button>`,
  check: id => `<button data-wd-check="${esc(id)}" class="btn btn-stone text-[11px] px-2 py-1">Check status</button>`,
  resolve: id => `<button data-wd-resolve="${esc(id)}" class="btn btn-stone text-[11px] px-2 py-1">Resolve</button>`,
};

export async function mountWithdrawals(container: HTMLElement): Promise<() => void> {
  let alive = true;
  let tab: Tab = 'withdrawals';

  let wdRows: WithdrawalRow[] = [];
  let wdPage = 1;
  let wdTotalPages = 1;
  let wdStatus = '';
  let wdError: string | null = null;

  let kycUsers: KycUser[] = [];
  let kycPage = 1;
  let kycTotalPages = 1;
  let kycStatus = 'PENDING';
  let kycError: string | null = null;
  // The queue is read the first time its tab opens, not on every mount.
  let kycLoaded = false;

  // One money action at a time: a double tap on touch used to send two
  // gateway polls / settles and two list reloads.
  let busy = false;

  // Kept across re-renders: a result that vanished with the next render was
  // as good as no result (2026-10-19).
  let message = '';
  let messageIsError = false;

  function msg(text: string, error = false): void {
    message = text;
    messageIsError = error;
    const el = container.querySelector<HTMLElement>('#wd-msg');
    if (el) {
      el.textContent = text;
      el.className = `text-[13px] mt-3 ${error ? 'text-red-400' : 'text-muted'}`;
    }
  }

  // Failures used to be swallowed into an empty list, which reads as "nothing
  // to review" — the worst possible lie on a payouts screen (2026-10-19).
  async function loadWithdrawals(): Promise<void> {
    if (!alive) return;
    try {
      const params: Record<string, string | number | undefined> = { page: wdPage, per_page: PAGE_SIZE };
      if (wdStatus) params.status = wdStatus;
      const res = await getWithdrawals(params);
      wdRows = res.items;
      wdTotalPages = Math.ceil(res.total / res.pageSize) || 1;
      wdError = null;
    } catch (err) {
      wdRows = [];
      wdError = cmsErrorText(err);
    }
  }

  async function loadKyc(): Promise<void> {
    if (!alive) return;
    try {
      const params: Record<string, string | number | undefined> = { page: kycPage, per_page: PAGE_SIZE };
      if (kycStatus) params.status = kycStatus;
      const res = await getKycUsers(params);
      kycUsers = res.items;
      kycLoaded = true;
      kycTotalPages = Math.ceil(res.total / res.pageSize) || 1;
      kycError = null;
    } catch (err) {
      kycUsers = [];
      kycError = cmsErrorText(err);
    }
  }

  function statusColor(s: string): 'green' | 'red' | 'yellow' | 'gray' {
    if (s === 'APPROVED' || s === 'COMPLETED') return 'green';
    if (s === 'PENDING' || s === 'PROCESSING') return 'yellow';
    if (s === 'REJECTED' || s === 'FAILED') return 'red';
    return 'gray';
  }

  function filterBar(attr: string, values: string[], current: string): string {
    return `<div class="flex gap-1 mb-3 flex-wrap" role="group" aria-label="Filter by status">${values
      .map(s => `<button ${attr}="${s}" aria-pressed="${s === current}"
        class="px-3 min-h-11 min-w-11 rounded text-[12px] ${s === current ? 'bg-gold/20 text-gold' : 'text-muted hover:text-text'}"
        >${s || 'All'}</button>`).join('')}</div>`;
  }

  function errorState(what: string, error: string, retryAttr: string): string {
    return `
      <div class="frame-inset p-6 text-center" role="alert">
        <p class="text-red-400 text-[13px] mb-3">Failed to load ${esc(what)}: ${esc(error)}</p>
        <button ${retryAttr} class="btn btn-stone text-[13px] px-4 min-h-11">Retry</button>
      </div>`;
  }

  function renderWithdrawals(): string {
    if (wdError) return filterBar('data-wd-filter', WD_FILTERS, wdStatus) + errorState('withdrawals', wdError, 'data-wd-retry');

    const rows = wdRows.map(r => {
      const actions = withdrawalActions(r).map(a => ACTION_BUTTON[a](r.id)).join('');
      return [
        formatDate(r.createdAt),
        esc(withdrawalPlayer(r)),
        `<span class="whitespace-nowrap">${formatMoney(r.amountCents, esc(r.currency))}</span>`,
        esc(r.payoutMethod ?? '—'),
        badge(r.status, statusColor(r.status)),
        actions ? `<div class="flex gap-1 flex-wrap">${actions}</div>` : '',
      ];
    });

    return `
      ${filterBar('data-wd-filter', WD_FILTERS, wdStatus)}
      ${adminTable(['Date', 'Player', 'Amount', 'Method', 'Status', 'Actions'], rows)}
      ${pagination(wdPage, wdTotalPages)}`;
  }

  function renderKyc(): string {
    if (kycError) return filterBar('data-kyc-filter', KYC_FILTERS, kycStatus) + errorState('the KYC queue', kycError, 'data-kyc-retry');

    const rows = kycUsers.map(u => [
      esc(u.username ?? u.email),
      esc(u.kycFullName ?? '—'),
      esc(u.kycCountry ?? '—'),
      statusBadge(u.kycStatus),
      u.kycSubmittedAt ? formatDate(u.kycSubmittedAt) : '—',
      u.kycStatus === 'PENDING'
        ? `<div class="flex gap-1">
            <button data-kyc-approve="${esc(u.id)}" class="btn btn-gold text-[11px] px-2 py-1">Approve</button>
            <button data-kyc-reject="${esc(u.id)}" class="btn btn-stone text-[11px] px-2 py-1">Reject</button>
          </div>`
        : '',
    ]);

    return `
      ${filterBar('data-kyc-filter', KYC_FILTERS, kycStatus)}
      ${adminTable(['Player', 'Full Name', 'Country', 'Status', 'Submitted', 'Actions'], rows)}
      ${pagination(kycPage, kycTotalPages)}`;
  }

  function render(): void {
    if (!alive) return;

    const tabBtn = (id: Tab, label: string) =>
      `<button data-wd-tab="${id}" class="px-4 min-h-11 text-[13px] font-heading ${
        tab === id ? 'text-gold border-b-2 border-gold' : 'text-muted hover:text-text'
      }">${label}</button>`;

    container.innerHTML = `
      <h2 class="font-heading text-gold text-lg mb-4">Payouts & KYC</h2>
      <div class="flex gap-1 border-b border-border/30 mb-4">
        ${tabBtn('withdrawals', 'Withdrawal Requests')}
        ${tabBtn('kyc', 'KYC Queue')}
      </div>
      <div id="wd-tab-body">
        ${tab === 'withdrawals' ? renderWithdrawals() : renderKyc()}
      </div>
      <div id="wd-msg" role="status"></div>
    `;
    if (message) msg(message, messageIsError);
    wire();
  }

  const reloadWithdrawals = () => loadWithdrawals().then(render);
  const reloadKyc = () => loadKyc().then(render);

  /** Runs a withdrawal action, reports the server's own error text, and always re-reads the list. */
  async function wdAction(work: () => Promise<string>): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      msg(await work());
    } catch (err) {
      msg(cmsErrorText(err), true);
    }
    try {
      await reloadWithdrawals();
    } finally {
      busy = false;
    }
  }

  /**
   * Core commits a KYC decision before e-mailing the player, and answers 500
   * when the e-mail fails — so "Failed" was often false (audit P3, core). A
   * 5xx is answered by re-reading the player's real status (2026-10-19).
   */
  async function kycAction(id: string, target: 'APPROVED' | 'REJECTED', work: () => Promise<void>): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      await runKycAction(id, target, work);
    } finally {
      busy = false;
    }
  }

  async function runKycAction(id: string, target: 'APPROVED' | 'REJECTED', work: () => Promise<void>): Promise<void> {
    const user = kycUsers.find(u => u.id === id);
    const who = user?.username ?? user?.email ?? 'player';
    const verb = target === 'APPROVED' ? 'approved' : 'rejected';
    try {
      await work();
      msg(`KYC ${verb} for ${who}.`);
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      let actual: string | null = null;
      if (status >= 500 && user) {
        try {
          const res = await getKycUsers({ search: user.email, per_page: PAGE_SIZE });
          actual = res.items.find(u => u.id === id)?.kycStatus ?? null;
        } catch { /* reported below as unknown */ }
      }
      if (actual === target) {
        msg(`KYC ${verb} for ${who} — status is now ${actual}. The server reported an error afterwards (${status}), most likely the notification e-mail; tell the player directly if needed.`);
      } else if (status >= 500) {
        msg(`${cmsErrorText(err)} — status is now ${actual ?? 'unknown'}; reload to confirm.`, true);
      } else {
        msg(cmsErrorText(err), true);
      }
    }
    await reloadKyc();
  }

  function on(attr: string, handler: (value: string, btn: HTMLButtonElement) => void): void {
    container.querySelectorAll<HTMLButtonElement>(`[${attr}]`).forEach(btn => {
      btn.addEventListener('click', () => handler(btn.getAttribute(attr) ?? '', btn));
    });
  }

  function wire(): void {
    on('data-wd-tab', v => {
      tab = v as Tab;
      msg('');
      if (tab === 'kyc' && !kycLoaded) void reloadKyc();
      else render();
    });

    on('data-wd-filter', v => { wdStatus = v; wdPage = 1; void reloadWithdrawals(); });
    on('data-kyc-filter', v => { kycStatus = v; kycPage = 1; void reloadKyc(); });
    on('data-wd-retry', () => void reloadWithdrawals());
    on('data-kyc-retry', () => void reloadKyc());

    on('data-page', v => {
      if (tab === 'withdrawals') { wdPage = Number(v); void reloadWithdrawals(); }
      else { kycPage = Number(v); void reloadKyc(); }
    });

    on('data-wd-approve', id => {
      const r = wdRows.find(x => x.id === id);
      showPrompt({
        title: 'Mark payout as paid',
        message: `Only do this after confirming in the gateway back office that ${
          esc(r ? formatMoney(r.amountCents, r.currency) : 'the payout')} reached ${esc(r ? withdrawalPlayer(r) : 'the player')}. `
          + 'It records the payout as APPROVED and issues an invoice — it does not send money.',
        label: 'Note (optional)',
        placeholder: 'e.g. confirmed in back office',
        confirmLabel: 'Mark paid',
        maxLength: 1000,
      }, note => wdAction(async () => {
        await approveWithdrawal(id, note || undefined);
        return 'Withdrawal marked paid; invoice issued.';
      }));
    });

    on('data-wd-reject', id => {
      showPrompt({
        title: 'Reject withdrawal',
        message: 'The held funds go back to the player.',
        label: 'Reason',
        placeholder: 'Reason for rejection',
        confirmLabel: 'Reject',
        minLength: 1,
        maxLength: 1000,
      }, reason => wdAction(async () => {
        await rejectWithdrawal(id, reason);
        return 'Withdrawal rejected; funds released.';
      }));
    });

    on('data-wd-check', (id, btn) => {
      if (busy) return;
      btn.disabled = true;
      void wdAction(async () => {
        const res = await checkWithdrawalStatus(id);
        const state = res.state ? ` (gateway: ${res.state})` : '';
        return `${OUTCOME_TEXT[res.outcome] ?? res.outcome}${state}`;
      });
    });

    on('data-wd-resolve', id => {
      showPrompt({
        title: 'Resolve stuck payout',
        message: 'Settle this payout by hand after checking the gateway back office. '
          + '"Paid" records it APPROVED with an invoice; "Not paid" releases the funds to the player.',
        // No default: "Paid" as the preselected answer meant a note alone
        // recorded the payout as paid and invoiced it.
        choice: {
          label: 'Gateway result',
          selected: '',
          options: [
            { value: '', label: 'Choose…' },
            { value: 'APPROVED', label: 'Paid' },
            { value: 'DECLINED', label: 'Not paid' },
          ],
        },
        label: 'Note (required)',
        placeholder: 'What the back office shows',
        confirmLabel: 'Resolve',
        minLength: 3,
        maxLength: 500,
      }, (note, state) => wdAction(async () => {
        const res = await resolveWithdrawal(id, state as 'APPROVED' | 'DECLINED', note);
        // 'failed' here is the operator's own "Not paid", not a gateway decline.
        if (res.outcome === 'failed') return 'Marked not paid — funds released to the player.';
        return OUTCOME_TEXT[res.outcome] ?? res.outcome;
      }));
    });

    on('data-kyc-approve', id => {
      showConfirm('Approve KYC for this player?', () =>
        kycAction(id, 'APPROVED', () => approveKyc(id)));
    });

    on('data-kyc-reject', id => {
      showPrompt({
        title: 'Reject KYC',
        message: 'The player is e-mailed this reason.',
        label: 'Reason',
        placeholder: 'Reason for rejection',
        confirmLabel: 'Reject',
        minLength: 1,
        maxLength: 1000,
      }, reason => kycAction(id, 'REJECTED', () => rejectKyc(id, reason)));
    });
  }

  await loadWithdrawals();
  render();
  return () => { alive = false; };
}
